import type { Platform, ThreadMessage } from '@rp/contracts'
import { Badge } from '../../design'
import { formatRelative } from '../../lib/time'
import { PLATFORM_LABELS } from '../queue/labels'
import { ITEM_COPY } from './copy'

const isHidden = (message: ThreadMessage): boolean => message.removedAt !== null || message.redactedAt !== null

const kindsOf = (message: ThreadMessage) => message.attachmentKinds ?? []

const attachmentNoteOf = (message: ThreadMessage): string | null => {
  const kinds = kindsOf(message)
  if (isHidden(message) || kinds.length === 0) return null
  return ITEM_COPY.attachmentSent[message.direction](kinds.map((kind) => ITEM_COPY.attachmentKinds[kind]))
}

const bodyOf = (message: ThreadMessage): string | null => {
  if (message.removedAt !== null) return ITEM_COPY.removed
  if (message.redactedAt !== null) return ITEM_COPY.redacted
  if (message.text !== null) return message.text
  return kindsOf(message).length === 0 ? ITEM_COPY.noText : null
}

const MessageBody = ({ message }: { message: ThreadMessage }) => {
  const body = bodyOf(message)
  const note = attachmentNoteOf(message)
  return (
    <>
      {body === null ? null : <p className="thread-body">{body}</p>}
      {note === null ? null : <p className="thread-body thread-attachment">{note}</p>}
    </>
  )
}

type ThreadViewProps = { platform: Platform; messages: readonly ThreadMessage[]; now: Date; olderMessageCount?: number }

export const ThreadView = ({ platform, messages, now, olderMessageCount = 0 }: ThreadViewProps) => (
  <>
    <h2 className="thread-label">{ITEM_COPY.thread}</h2>
    {olderMessageCount > 0 ? <p className="thread-older">{ITEM_COPY.olderMessagesHidden(olderMessageCount)}</p> : null}
    <ul className="thread" aria-label={ITEM_COPY.thread}>
      {messages.map((message) => (
        <li key={message.id} className={`thread-message thread-${message.direction}`}>
          <p className="thread-head">
            <span className="thread-author">{message.authorName}</span>
            <span className="thread-time num">{formatRelative(new Date(message.sentAt), now)}</span>
            {message.editedAt === null ? null : <Badge tone="warning">{ITEM_COPY.edited}</Badge>}
            {message.sentOutside ? <Badge>{ITEM_COPY.sentOutside(PLATFORM_LABELS[platform])}</Badge> : null}
          </p>
          <MessageBody message={message} />
        </li>
      ))}
    </ul>
  </>
)
